import { useState } from "react";
import { C, PageHeader, GlassCard, Badge, Button, Toggle, Skeleton, StatusDot } from "@coremonit/design-system";
import { Bell, Send, Slack, MessageCircle, Ticket, MailIcon, Radio } from "lucide-react";
import { useAlertRules, useIncidents } from "../api/hooks";

const CHANNEL_ICON: Record<string, React.ReactNode> = {
  slack: <Slack size={11} />, pagerduty: <Radio size={11} />, email: <MailIcon size={11} />, teams: <MessageCircle size={11} />, jira: <Ticket size={11} />,
};

export function AlertsPage() {
  const rules = useAlertRules();
  const incidents = useIncidents();
  const [enabledDraft, setEnabledDraft] = useState<Record<string, boolean>>({});

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader icon={<Bell size={16} />} title="Alerting" subtitle="Rules · preview · notification routing into the Integration Hub" />

      <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 12 }}>
        <GlassCard style={{ padding: 14 }}>
          <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>Alert rules</div>
          {rules.isLoading ? <Skeleton height={240} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {rules.data?.items.map((r) => {
                const enabled = enabledDraft[r.id] ?? r.enabled;
                return (
                  <div key={r.id} style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 10, alignItems: "center", padding: 10, background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ color: C.text, fontSize: 12, fontWeight: 700 }}>{r.name}</div>
                        <Badge color={r.severity === "critical" ? "red" : r.severity === "high" ? "amber" : "muted"}>{r.severity}</Badge>
                      </div>
                      <div style={{ color: C.textMuted, fontSize: 10, marginTop: 3 }}>
                        <span style={{ fontFamily: "ui-monospace, monospace" }}>{r.metric} {r.operator} {r.threshold}</span> · window {r.window} · {r.serviceIds.length} services
                      </div>
                      <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                        {r.routes.map((rt, i) => (
                          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 6px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 9, color: C.textSub }}>
                            {CHANNEL_ICON[rt.channel]} <span style={{ color: C.text }}>{rt.target}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <Toggle value={enabled} onChange={(v) => setEnabledDraft((d) => ({ ...d, [r.id]: v }))} />
                  </div>
                );
              })}
            </div>
          )}
        </GlassCard>

        <GlassCard style={{ padding: 14 }}>
          <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>Recent incidents</div>
          {incidents.isLoading ? <Skeleton height={240} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 500, overflow: "auto" }}>
              {incidents.data?.items.map((i) => (
                <div key={i.id} style={{ padding: 10, background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                      <StatusDot status={i.severity === "critical" ? "critical" : "warning"} pulse={i.status === "firing"} />
                      <div style={{ color: C.text, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{i.title}</div>
                    </div>
                    <Badge color={i.status === "firing" ? "red" : i.status === "acknowledged" ? "amber" : "green"}>{i.status}</Badge>
                  </div>
                  <div style={{ color: C.textMuted, fontSize: 10, marginTop: 4 }}>{i.ruleName} · started {new Date(i.startedAt).toLocaleTimeString()}</div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
